//#region bunja.ts
const __DEV__ = process.env.NODE_ENV !== "production";
const bunja = bunjaFn;
function bunjaFn(init) {
	return new Bunja(init);
}
bunjaFn.use = invalidUse;
bunjaFn.fork = invalidFork;
bunjaFn.effect = invalidEffect;
function createScope(hash) {
	return new Scope(hash);
}
function createBunjaStore(config) {
	const { wrapInstance = defaultWrapInstanceFn } = config ?? {};
	const store = new BunjaStore();
	store.wrapInstance = wrapInstance;
	return store;
}
function invalidUse() {
	throw new Error("`bunja.use` can only be used inside a bunja init function.");
}
function invalidFork() {
	throw new Error("`bunja.fork` can only be used inside a bunja init function.");
}
function invalidEffect() {
	throw new Error("`bunja.effect` can only be used inside a bunja init function.");
}
const defaultWrapInstanceFn = (fn) => fn(noop);
var BunjaStore = class BunjaStore {
	static counter = 0;
	id = String(BunjaStore.counter++);
	#bunjas = {};
	#scopes = /* @__PURE__ */ new Map();
	#bakingContext;
	wrapInstance = defaultWrapInstanceFn;
	constructor() {
		if (__DEV__) {
			devtoolsGlobalHook.stores[this.id] = this;
			devtoolsGlobalHook.emit("storeCreated", { storeId: this.id });
		}
	}
	get _internalState() {
		if (__DEV__) return {
			bunjas: this.#bunjas,
			scopes: this.#scopes
		};
		return void 0;
	}
	dispose() {
		for (const instance of Object.values(this.#bunjas)) instance.dispose();
		for (const instanceMap of Object.values(this.#scopes)) for (const instance of instanceMap.values()) instance.dispose();
		this.#bunjas = {};
		this.#scopes = /* @__PURE__ */ new Map();
		if (__DEV__) {
			devtoolsGlobalHook.emit("storeDisposed", { storeId: this.id });
			delete devtoolsGlobalHook.stores[this.id];
		}
	}
	get(bunja$1, readScope) {
		const originalUse = bunjaFn.use;
		try {
			const { bunjaInstance, bunjaInstanceMap, scopeInstanceMap } = bunja$1.baked ? this.#getBaked(bunja$1, readScope) : this.#getUnbaked(bunja$1, readScope);
			const result = {
				value: bunjaInstance.value,
				mount: () => {
					bunjaInstanceMap.forEach((instance) => instance.add());
					bunjaInstance.add();
					scopeInstanceMap.forEach((instance) => instance.add());
					const unmount = () => {
						bunjaInstanceMap.forEach((instance) => instance.sub());
						bunjaInstance.sub();
						scopeInstanceMap.forEach((instance) => instance.sub());
					};
					return unmount;
				},
				deps: Array.from(scopeInstanceMap.values()).map(({ value }) => value)
			};
			if (__DEV__) {
				result.bunjaInstance = bunjaInstance;
				devtoolsGlobalHook.emit("getCalled", {
					storeId: this.id,
					bunjaInstanceId: bunjaInstance.id
				});
			}
			return result;
		} finally {
			bunjaFn.use = originalUse;
		}
	}
	#getBaked(bunja$1, readScope) {
		const scopeInstanceMap = new Map(bunja$1.relatedScopes.map((scope) => [scope, this.#getScopeInstance(scope, readScope(scope))]));
		const bunjaInstanceMap = /* @__PURE__ */ new Map();
		bunjaFn.use = (dep) => {
			if (dep instanceof Bunja) return bunjaInstanceMap.get(dep).value;
			if (dep instanceof Scope) return scopeInstanceMap.get(dep).value;
			throw new Error("`bunja.use` can only be used with Bunja or Scope.");
		};
		for (const relatedBunja of bunja$1.relatedBunjas) bunjaInstanceMap.set(relatedBunja, this.#getBunjaInstance(relatedBunja, scopeInstanceMap));
		const bunjaInstance = this.#getBunjaInstance(bunja$1, scopeInstanceMap);
		return {
			bunjaInstance,
			bunjaInstanceMap,
			scopeInstanceMap
		};
	}
	#getUnbaked(bunja$1, readScope) {
		const bunjaInstanceMap = /* @__PURE__ */ new Map();
		const scopeInstanceMap = /* @__PURE__ */ new Map();
		function getUse(map, addDep, getInstance) {
			return (dep) => {
				const d = dep;
				addDep(d);
				if (map.has(d)) return map.get(d).value;
				const instance = getInstance(d);
				map.set(d, instance);
				return instance.value;
			};
		}
		const useScope = getUse(scopeInstanceMap, (dep) => this.#bakingContext.currentBunja.addScope(dep), (dep) => this.#getScopeInstance(dep, readScope(dep)));
		const useBunja = getUse(bunjaInstanceMap, (dep) => this.#bakingContext.currentBunja.addParent(dep), (dep) => {
			if (dep.baked) for (const scope of dep.relatedScopes) useScope(scope);
			return this.#getBunjaInstance(dep, scopeInstanceMap);
		});
		bunjaFn.use = (dep) => {
			if (dep instanceof Bunja) return useBunja(dep);
			if (dep instanceof Scope) return useScope(dep);
			throw new Error("`bunja.use` can only be used with Bunja or Scope.");
		};
		const originalBakingContext = this.#bakingContext;
		try {
			this.#bakingContext = { currentBunja: bunja$1 };
			const bunjaInstance = this.#getBunjaInstance(bunja$1, scopeInstanceMap);
			return {
				bunjaInstance,
				bunjaInstanceMap,
				scopeInstanceMap
			};
		} finally {
			this.#bakingContext = originalBakingContext;
		}
	}
	#getBunjaInstance(bunja$1, scopeInstanceMap) {
		const originalEffect = bunjaFn.effect;
		const originalFork = bunjaFn.fork;
		const prevBunja = this.#bakingContext?.currentBunja;
		try {
			const effects = [];
			bunjaFn.effect = (callback) => {
				effects.push(callback);
			};
			bunjaFn.fork = (b, scopeValuePairs) => {
				const readScope = createReadScopeFn(scopeValuePairs, bunjaFn.use);
				const { value, mount } = this.get(b, readScope);
				bunjaFn.effect(mount);
				return value;
			};
			if (this.#bakingContext) this.#bakingContext.currentBunja = bunja$1;
			if (bunja$1.baked) {
				const id = bunja$1.calcInstanceId(scopeInstanceMap);
				if (id in this.#bunjas) return this.#bunjas[id];
				return this.wrapInstance((dispose) => {
					const value = bunja$1.init();
					return this.#createBunjaInstance(id, value, effects, dispose);
				});
			} else return this.wrapInstance((dispose) => {
				const value = bunja$1.init();
				bunja$1.bake();
				const id = bunja$1.calcInstanceId(scopeInstanceMap);
				return this.#createBunjaInstance(id, value, effects, dispose);
			});
		} finally {
			bunjaFn.effect = originalEffect;
			bunjaFn.fork = originalFork;
			if (this.#bakingContext) this.#bakingContext.currentBunja = prevBunja;
		}
	}
	#getScopeInstance(scope, value) {
		const key = scope.hash(value);
		const instanceMap = this.#scopes.get(scope) ?? this.#scopes.set(scope, /* @__PURE__ */ new Map()).get(scope);
		return instanceMap.get(key) ?? instanceMap.set(key, this.#createScopeInstance(scope, key, value, () => {
			instanceMap.delete(key);
			if (__DEV__) devtoolsGlobalHook.emit("scopeInstanceUnmounted", {
				storeId: this.id,
				scope,
				key
			});
		})).get(key);
	}
	#createBunjaInstance(id, value, effects, dispose) {
		const effect = () => {
			const cleanups = effects.map((effect$1) => effect$1()).filter(Boolean);
			return () => cleanups.forEach((cleanup) => cleanup());
		};
		const bunjaInstance = new BunjaInstance(id, value, effect, () => {
			if (__DEV__) devtoolsGlobalHook.emit("bunjaInstanceUnmounted", {
				storeId: this.id,
				bunjaInstanceId: id
			});
			dispose();
			delete this.#bunjas[id];
		});
		this.#bunjas[id] = bunjaInstance;
		if (__DEV__) devtoolsGlobalHook.emit("bunjaInstanceMounted", {
			storeId: this.id,
			bunjaInstanceId: id
		});
		return bunjaInstance;
	}
	#createScopeInstance(scope, key, value, dispose) {
		if (__DEV__) devtoolsGlobalHook.emit("scopeInstanceMounted", {
			storeId: this.id,
			scope,
			key
		});
		return new ScopeInstance(value, dispose);
	}
};
function createReadScopeFn(scopeValuePairs, readScope) {
	const map = new Map(scopeValuePairs);
	return (scope) => {
		if (map.has(scope)) return map.get(scope);
		return readScope(scope);
	};
}
function delayUnmount(mount, ms = 0) {
	return () => {
		const unmount = mount();
		return () => setTimeout(unmount, ms);
	};
}
var Bunja = class Bunja {
	static counter = 0;
	id = String(Bunja.counter++);
	debugLabel = "";
	#phase = {
		baked: false,
		parents: /* @__PURE__ */ new Set(),
		scopes: /* @__PURE__ */ new Set()
	};
	constructor(init) {
		this.init = init;
	}
	get baked() {
		return this.#phase.baked;
	}
	get parents() {
		if (this.#phase.baked) return this.#phase.parents;
		return Array.from(this.#phase.parents);
	}
	get relatedBunjas() {
		if (!this.#phase.baked) throw new Error("Bunja is not baked yet.");
		return this.#phase.relatedBunjas;
	}
	get relatedScopes() {
		if (!this.#phase.baked) throw new Error("Bunja is not baked yet.");
		return this.#phase.relatedScopes;
	}
	addParent(bunja$1) {
		if (this.#phase.baked) return;
		this.#phase.parents.add(bunja$1);
	}
	addScope(scope) {
		if (this.#phase.baked) return;
		this.#phase.scopes.add(scope);
	}
	bake() {
		if (this.#phase.baked) throw new Error("Bunja is already baked.");
		const scopes = this.#phase.scopes;
		const parents = this.parents;
		const relatedBunjas = toposort(parents);
		const relatedScopes = Array.from(new Set([...relatedBunjas.flatMap((bunja$1) => bunja$1.relatedScopes), ...scopes]));
		this.#phase = {
			baked: true,
			parents,
			relatedBunjas,
			relatedScopes
		};
	}
	calcInstanceId(scopeInstanceMap) {
		const scopeInstanceIds = this.relatedScopes.map((scope) => scopeInstanceMap.get(scope).id);
		return `${this.id}:${scopeInstanceIds.join(",")}`;
	}
	toString() {
		const { id, debugLabel } = this;
		return `[Bunja:${id}${debugLabel && ` - ${debugLabel}`}]`;
	}
};
var Scope = class Scope {
	static counter = 0;
	id = String(Scope.counter++);
	debugLabel = "";
	constructor(hash = Scope.identity) {
		this.hash = hash;
	}
	static identity(x) {
		return x;
	}
	bind(value) {
		return [this, value];
	}
	toString() {
		const { id, debugLabel } = this;
		return `[Scope:${id}${debugLabel && ` - ${debugLabel}`}]`;
	}
};
var RefCounter = class {
	#count = 0;
	add() {
		++this.#count;
	}
	sub() {
		--this.#count;
		if (this.#count < 1) {
			this.dispose();
			this.dispose = noop;
		}
	}
};
var BunjaInstance = class extends RefCounter {
	#cleanup;
	constructor(id, value, effect, _dispose) {
		super();
		this.id = id;
		this.value = value;
		this.effect = effect;
		this._dispose = _dispose;
	}
	dispose() {
		this.#cleanup?.();
		this._dispose();
	}
	add() {
		this.#cleanup ??= this.effect() ?? noop;
		super.add();
	}
};
var ScopeInstance = class ScopeInstance extends RefCounter {
	static counter = 0;
	id = String(ScopeInstance.counter++);
	constructor(value, dispose) {
		super();
		this.value = value;
		this.dispose = dispose;
	}
};
function toposort(nodes) {
	const visited = /* @__PURE__ */ new Set();
	const result = [];
	function visit(current) {
		if (visited.has(current)) return;
		visited.add(current);
		for (const parent of current.parents) visit(parent);
		result.push(current);
	}
	for (const node of nodes) visit(node);
	return result;
}
const noop = () => {};
let devtoolsGlobalHook;
if (__DEV__) if (globalThis.__BUNJA_DEVTOOLS_GLOBAL_HOOK__) devtoolsGlobalHook = globalThis.__BUNJA_DEVTOOLS_GLOBAL_HOOK__;
else {
	devtoolsGlobalHook = {
		stores: {},
		listeners: {
			storeCreated: /* @__PURE__ */ new Set(),
			storeDisposed: /* @__PURE__ */ new Set(),
			getCalled: /* @__PURE__ */ new Set(),
			bunjaInstanceMounted: /* @__PURE__ */ new Set(),
			bunjaInstanceUnmounted: /* @__PURE__ */ new Set(),
			scopeInstanceMounted: /* @__PURE__ */ new Set(),
			scopeInstanceUnmounted: /* @__PURE__ */ new Set()
		},
		emit: (type, event) => {
			for (const fn of devtoolsGlobalHook.listeners[type]) fn(event);
		},
		on: (type, listener) => {
			devtoolsGlobalHook.listeners[type].add(listener);
			return () => devtoolsGlobalHook.listeners[type].delete(listener);
		}
	};
	globalThis.__BUNJA_DEVTOOLS_GLOBAL_HOOK__ = devtoolsGlobalHook;
}

//#endregion
export { Bunja, BunjaStore, Scope, bunja, createBunjaStore, createReadScopeFn, createScope, delayUnmount };